import { useState, useEffect, type FC } from "react";
import { MdCloudQueue, MdEmail, MdFolder, MdOutlineCreateNewFolder, MdSearch, MdClose } from "react-icons/md";
import "../styles/SaveDrawer.css";
import { getSelectedEmailsInfo, getSetting } from "../taskpane";
import { getUserFavorites, saveEmailsToFolder } from "../../apiClient";
import { useAppContext } from "../contexts/AppContext";
import type { MinimalEmailData, FolderItem } from "../../../../common/src/types/types";

const SaveDrawer: FC = () => {
    const {
        navigateToFolders,
        handleSaveEmails,
        openedProject,
        bodyStoragePrefs,
        selectedFolders: contextSelectedFolders,
        setSelectedFolders,
    } = useAppContext();

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedEmails, setSelectedEmails] = useState<MinimalEmailData[]>([]);
    const [favoriteFolders, setFavoriteFolders] = useState<FolderItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSuccess, setToastSuccess] = useState(true);
    const [deleteAfterArchive, setDeleteAfterArchive] = useState(getSetting("deleteArchivedEmails", false));
    const selectedFolders = contextSelectedFolders.length > 0
        ? contextSelectedFolders
        : openedProject
            ? [openedProject]
            : [];

    // Load favorites from API
    useEffect(() => {
        let isActive = true;
        const loadFavorites = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const favorites = await getUserFavorites();
                if (isActive) {
                    setFavoriteFolders(favorites || []);
                }
            } catch (err) {
                if (isActive) {
                    setError("Failed to load folders. Please try again.");
                    console.error("Error loading favorites:", err);
                }
            } finally {
                if (isActive) {
                    setIsLoading(false);
                }
            }
        };

        loadFavorites();
        return () => {
            isActive = false;
        };
    }, []);

    const filteredFolders = favoriteFolders.filter(
        (folder) =>
            folder.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (folder.projectNumber && folder.projectNumber.includes(searchTerm))
    );
    const selectableFolders = filteredFolders
        .filter((folder) => folder.id !== null)
        .map((folder) => ({ ...folder, id: folder.id as string }));

    // Load selected emails on component mount and listen for changes
    useEffect(() => {
        const loadEmails = async () => {
            const emails = await getSelectedEmailsInfo();
            setSelectedEmails(emails);
        };

        loadEmails();

        // Listen for email selection changes
        const handleEmailSelectionUpdate = (event: CustomEvent) => {
            setSelectedEmails(event.detail.emails);
        };

        const handleOneDriveUploadComplete = (event: CustomEvent) => {
            const { message, success } = event.detail;
            setToastMessage(message);
            setToastSuccess(success);
            setShowToast(true);
        };

        window.addEventListener('email-selection-changed', handleEmailSelectionUpdate as EventListener);
        //window.addEventListener('onedrive-upload-complete', handleOneDriveUploadComplete as EventListener);

        return () => {
            window.removeEventListener('email-selection-changed', handleEmailSelectionUpdate as EventListener);
            //window.removeEventListener('onedrive-upload-complete', handleOneDriveUploadComplete as EventListener);
        };
    }, []);

    const handleSave = async () => {
        if (selectedFolders.length === 0 || selectedEmails.length === 0 || isSaving) return;

        const selectedFolderItems = selectedFolders
            .map(folderId => favoriteFolders.find(folder => folder.id === folderId))
            .filter((folder): folder is FolderItem => Boolean(folder));
        if (selectedFolderItems.length !== selectedFolders.length) {
            setError("Selected folder not found.");
            return;
        }

        try {
            setIsSaving(true);
            for (const folder of selectedFolderItems) {
                if (folder.id && bodyStoragePrefs[folder.id] === false) {
                    const emailsToSave = selectedEmails.map(email => ({ ...email, body: undefined, bodyRemoved: true }));
                    await saveEmailsToFolder(folder.id, emailsToSave, deleteAfterArchive);
                } else if (folder.id) {
                    await saveEmailsToFolder(folder.id, selectedEmails, deleteAfterArchive);
                }
            }
            handleSaveEmails(selectedFolders[0], selectedEmails);
            setSelectedFolders([]);
        } catch (error) {
            console.error("Failed to save emails:", error);
            setError("Failed to save emails. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="save-drawer">
            <div className="save-drawer-header">
                <button className="back-button" onClick={navigateToFolders}>
                    ← Back
                </button>
                <h2>Archive Emails</h2>
            </div>

            <div className="content">
                <div className="section">
                    <h3>
                        <MdEmail className="sectionIcon" />
                        Selected Emails ({selectedEmails.length})
                    </h3>
                    <div className="emailList">
                        {selectedEmails.map((email) => (
                            <div key={email.id} className="emailItem">
                                <div className="emailSubject">{email.subject}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="section">
                    <h3>
                        <MdFolder className="sectionIcon" />
                        Select Archive Folder
                    </h3>
                    <div className="searchContainer">
                        <MdSearch className="searchIcon" />
                        <input
                            type="text"
                            placeholder="Search projects by name, number, or PM..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="searchInput"
                            disabled={isLoading}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="search-clear-button"
                                aria-label="Clear search"
                                title="Clear search"
                                onClick={() => setSearchTerm("")}
                            >
                                <MdClose />
                            </button>
                        )}
                    </div>

                    {isLoading && (
                        <div className="loading-state">
                            <p>Loading folders...</p>
                        </div>
                    )}

                    {error && !isLoading && (
                        <div className="error-state">
                            <p>{error}</p>
                        </div>
                    )}

                    {!isLoading && !error && filteredFolders.length === 0 && (
                        <div className="empty-state">
                            <p>No folders found. {searchTerm ? 'Try a different search.' : 'Add some favorites first.'}</p>
                        </div>
                    )}

                    {!isLoading && !error && selectableFolders.length > 0 && (
                        <div className="projectList">
                            {[...selectableFolders].sort((a, b) => (selectedFolders.includes(a.id) ? -1 : 1) - (selectedFolders.includes(b.id) ? -1 : 1)).map((folder) => (
                                <div
                                    key={folder.id}
                                    className={`projectItem ${selectedFolders.includes(folder.id) ? "selected" : ""}`.trim()}
                                    onClick={() => {
                                        setSelectedFolders((current) => current.includes(folder.id)
                                            ? current.filter((folderId) => folderId !== folder.id)
                                            : [...current, folder.id]);
                                    }}
                                >
                                    <div className="projectHeader">
                                        {folder.favoriteType === "project" && <MdFolder className="folder-type-icon project" title="Project Folder" />}
                                        {folder.favoriteType === "onedrive" && <MdCloudQueue className="folder-type-icon onedrive" title="OneDrive Folder" />}
                                        {folder.favoriteType === "custom" && <MdOutlineCreateNewFolder className="folder-type-icon custom" title="Custom Folder" />}
                                        <h4 className="folder-name" title={folder.name}>{folder.name}</h4>
                                    </div>
                                    <div className="projectMeta"></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="footer">
                <input
                    type="checkbox"
                    id="deleteAfterArchive"
                    checked={deleteAfterArchive}
                    onChange={(e) => {
                        setDeleteAfterArchive(e.target.checked);
                    }}
                    disabled={isSaving}
                />
                <label htmlFor="deleteAfterArchive" className="deleteAfterArchiveLabel">
                    Delete After?
                </label>
                <button className="cancelButton" onClick={navigateToFolders} disabled={isSaving}>
                    Cancel
                </button>
                <button
                    className="saveButton"
                    onClick={handleSave}
                    disabled={selectedFolders.length === 0 || selectedEmails.length === 0 || isSaving}
                >
                    {isSaving ? "Saving..." : "Save to Archive"}
                </button>
            </div>

            {/* Toast notification */}
            {showToast && (
                <div className={`toast ${toastSuccess ? 'success' : 'error'} show`}>
                    <span>{toastMessage}</span>
                    <button
                        className="toast-close"
                        onClick={() => setShowToast(false)}
                        aria-label="Close notification"
                    >
                        ×
                    </button>
                </div>
            )}
        </div>
    );
};

export default SaveDrawer;
